import React, { useState } from "react";
import Checkbox from "@mui/material/Checkbox";
import { Box, Typography, FormControl, Grid } from "@mui/material";
import { SelectChangeEvent } from "@mui/material/Select";
import { Tag, TagCategory } from "../../gen/api";
import { TreeView, TreeItem } from '@mui/x-tree-view';
import { isLeaf } from "@mui/x-data-grid";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 6.5 + ITEM_PADDING_TOP,
      width: 350,
    },
  },
};

type Props = {
    cluster: TagCategory
    selectedTags?: string[]
    onClick?: (event: React.ChangeEvent<HTMLInputElement>) => void
    onToggleSubtree?: (tag: Tag, checked: boolean) => void
    //children: React.ReactNode
}



const zielindikatorenOrder = [
  'Psychische- und Verhaltensstörungen',
  'Globale Gesundheit',
  'Mentale und Soziale Gesundheit',
  'Physische Gesundheit',
  'Elterliche Belastung',
];

const zielindikatorenChildOrders: Record<string, string[]> = {
  'Psychische- und Verhaltensstörungen': [
    "Störungsübergreifende Verfahren",
    "Affektive Störungen",
    "Angststörungen",
    "Aufmerksamkeitsdefizit/Hyperaktivitätsstörung",
    "Autismus-Spektrum- und Soziale Kommunikations-Störungen",
    "Bindungsstörungen",
    "Essstörungen",
    "Internetbezogene Störungen",
    "Persönlichkeitsstörungen",
    "Psychotische Störungen",
    "Selbstverletztendes Verhalten",
    "Somatoforme Störungen",
    "Störung des Sozialverhaltens",
    "Sucht- und subtanzbezogene Störungen",
    "Suizidalität",
    "Ticstörungen",
    "Trauma- und belastungsbezogene Störungen",
    "Zwangsstörungen"
    ],
  'Globale Gesundheit': ["Gesundheitsbezogene Lebensqualität (HRQOL)"],
  'Mentale und Soziale Gesundheit': [
    "Einsamkeit",
    "Emotionsregulation",
    "Ressourcen",
    "Selbstwert",
    "Selbstwirksamkeit & Resilienz",
    "Soziale Unterstützung und Beziehungen",
    "Stress"
    ],
  'Physische Gesundheit': ["Fatigue", "Körperliche Beschwerden", "Physische Aktivität / ADL","Schmerzen"]
};

const sortByCustomOrder = (items: Tag[], order: string | any[]) => {
  return [...items].sort((a, b) => {
    const ai = order.indexOf(a.name);
    const bi = order.indexOf(b.name);
    if (ai === -1 && bi === -1) return a.name.localeCompare(b.name); // fallback: alphabetical
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
};


export default function ClusterTreeSelect({cluster, selectedTags = [], onClick, onToggleSubtree}: Props) {

    // sort tags if they are in Zielindikatoren
    const isZielindikator = cluster.name === 'Zielindikatoren';
    const sortedTags = React.useMemo(() => {
        if (!cluster.tags) return [];

        let tags = cluster.tags;

        if (isZielindikator) {
            tags = sortByCustomOrder(tags, zielindikatorenOrder)
            // Sort children of each parent according to its own list
            tags = tags.map((tag) => {
            const childOrder = zielindikatorenChildOrders[tag.name];
            const sortedChildren = childOrder
                ? sortByCustomOrder(tag.childTags || [], childOrder)
                : (tag.childTags || []).sort((a, b) =>
                    a.name.localeCompare(b.name)
                );

            return { ...tag, childTags: sortedChildren };
            });
        }

        return tags;
    }, [cluster, isZielindikator])

    // compute all tag ids that belong to this cluster (parents and children)
    const clusterOptionIds = React.useMemo(() => {
        return sortedTags.flatMap(tag => {
            if (tag.childTags && tag.childTags.length > 0) {
                return tag.childTags.map(child => child.id!);
            }
            return [tag.id!];
        });
    }, [sortedTags]);
    // value must be the subset of selectedTags that belong to this cluster
    const value = selectedTags.filter(id => clusterOptionIds.includes(id));

    // collect all descendant ids of a tag
    const collectIds = (tag: Tag): string[] => [
        tag.id!,
        ...(tag.childTags?.flatMap(collectIds) ?? [])
    ];

    const collectSubtreeIds = (tag: Tag): string[] => [
        tag.id!,
        ...(tag.childTags?.flatMap(collectSubtreeIds) ?? []),
    ];

    const handleChange = (event: SelectChangeEvent<string[]>) => {
        if (!onClick) return;
        const raw = event.target.value;
        const newValue = typeof raw === 'string' ? raw.split(',') : (raw as string[]);
        const prevValue = value;

        // determine added and removed ids
        const added = newValue.filter(id => !prevValue.includes(id));
        const removed = prevValue.filter(id => !newValue.includes(id));

        // emit a synthetic checkbox-like event for each changed id so parent handler toggles them
        [...added, ...removed].forEach(id => {
            const synthetic = {
                target: { value: id }
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onClick(synthetic);
        });
    }

    const isNodeChecked = (tag: Tag, value: string[]): boolean => {
        //value.includes(tag.id!) ||
        //(tag.childTags?.every(child => isNodeChecked(child, value)) ?? false);
        if (!tag.childTags || tag.childTags.length === 0) {
            return value.includes(tag.id!);
        }

        return tag.childTags.every(child => isNodeChecked(child, value));
    };

    const isNodeIndeterminate = (tag: Tag, value: string[]): boolean => {
        //!isNodeChecked(tag, value) &&
        //(tag.childTags?.some(child => isNodeChecked(child, value)) ?? false);
        if (!tag.childTags || tag.childTags.length === 0) {
            return false;
        }

        const someChecked = tag.childTags.some(child => isNodeChecked(child, value));
        const allChecked = tag.childTags.every(child => isNodeChecked(child, value));
        return someChecked && !allChecked;
    }

    //const handleCheck = (id: string, checked: boolean) => {
    const handleCheck = (tag: Tag, checked: boolean) => {
        //const ids = collectIds(tag)
        const subtreeIds = collectSubtreeIds(tag);
        //let newValue= [...value];
        //const newValue = checked ? [...value, id] : value.filter(v => v !== id);
        const newValue = checked
            ? Array.from(new Set([...value, ...subtreeIds]))
            : value.filter(id => !subtreeIds.includes(id));

        React.startTransition(() => {
            subtreeIds.forEach(id => {
                const synthetic = { target: { value: id } } as React.ChangeEvent<HTMLInputElement>;
                onClick?.(synthetic);
            });
        });

        /* if (checked) {
            // add all ids
            subtreeIds.forEach(id => {
                if (!newValue.includes(id)) newValue.push(id);
            });
        } else {
            // remove all ids
            newValue = newValue.filter(id => !ids.includes(id));
            } 
        //determine added and removed ids and emit synthetic events
        const added = newValue.filter(id => !value.includes(id));
        const removed = value.filter(id => !newValue.includes(id));

        [...added, ...removed].forEach(id => {
            const synthetic = {
                target: { value: id }
            } as unknown as React.ChangeEvent<HTMLInputElement>;
            onClick && onClick(synthetic);
        }); */
    };

    // build tree items for RichTreeView
    const buildTreeItems = (tags: Tag[]) => {
        
        return tags.map(tag => (
            <TreeItem key={tag.id} nodeId={tag.id!} label={
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.25,
                        px: 0.25,
                        py: 0.13,
                        borderRadius: 0.25,
                        minHeight: 28,
                        '&hover': {bgcolor: 'action.hover'}
                    }}
                >
                
                    <Checkbox
                        //<div style={{display: 'flex', alignItems: 'center', fontSize: '0.875rem'}}>
                        //checked={value.includes(tag.id!)}
                        size='small'
                        sx={{p: 0}}
                        checked={isNodeChecked(tag, value)}
                        indeterminate={isNodeIndeterminate(tag, value)}
                        //onChange={(e) => handleCheck(tag.id!, e.target.checked)}
                        onChange={(e) => {
                            // if tag is leaf, just handle check, else use onToggleSubtree
                            if (!tag.childTags || tag.childTags.length === 0) {
                                handleCheck(tag, e.target.checked)
                            } else {
                                onToggleSubtree?.(tag, e.target.checked)
                            }
                        }}
                        onClick={e => e.stopPropagation()}
                    />
                <Typography variant="body2">{tag.name}</Typography>
                </Box>
                //</div>
            }>
                {tag.childTags && tag.childTags.length > 0 ? buildTreeItems(tag.childTags) : null}
            </TreeItem>
        ))
    }

    return (
        <Grid item xs={12} sm={6} md={4} lg={3} xl={1.5}>
            <FormControl sx={{ width: 360 }}>
                <Box sx={{ border: '1px sold', borderColor: 'divider', borderRadius: 0.25, maxHeight: 150, overflow: 'auto', p: 0.13, bgcolor: 'background.paper'}}>
                    <TreeView
                        disableSelection
                        sx={{'& .MuiTreeItem-root': {'&:focus > .MuiTreeItem-conten': {backgroundColor: 'action.hover'}}}}
                    >
                        {buildTreeItems(sortedTags)}
                    </TreeView>
                </Box>
            </FormControl>
        </Grid>
    )
}